<script setup lang="ts">
import pointerIcon from '../../assets/images/tooltip-pointer.svg'

import type { TooltipPositionX, TooltipPositionY } from './types'

type Props = {
  positionX?: TooltipPositionX
  positionY?: TooltipPositionY
  width?: number | string
  opacity?: number
  offset?: number
  pointer?: boolean
  pointerOffset?: number
  fixed?: boolean
  styled?: boolean
  active?: boolean
  deactivationTimeout?: number
}

const props = withDefaults(defineProps<Props>(), {
  positionX: 'right',
  positionY: 'bottom',
  pointerOffset: undefined,
  width: 200,
  opacity: 1,
  offset: 5,
  styled: true,
  active: false,
  deactivationTimeout: 0,
})

const isActive = ref(props.active)

const tooltipElement = ref<HTMLElement | null>(null)
const tooltipContentElement = ref<HTMLElement | null>(null)

const tooltipContentPosition = ref({
  top: '0',
  left: '0',
})

const tooltipContentStyles = computed(() => {
  return {
    ...tooltipContentPosition.value,
    width: `${props.width}px`,
    backgroundColor: `rgba(var(--ui-color-sandy-950-rgb), ${props.opacity})`,
  }
})

const tooltipPointerStyles = computed(() => {
  const { top, left } = getPointerPosition()

  return {
    top,
    left,
    opacity: props.opacity,
  }
})

const tooltipContentClasses = computed(() => {
  return [
    'app-tooltip__content',
    {
      'app-tooltip__content--fixed': props.fixed,
      'app-tooltip__content--styled': props.styled,
    },
  ]
})

const tooltipPointerClasses = computed(() => {
  return [
    'app-tooltip__pointer',
    `app-tooltip__pointer--x-${props.positionX}`,
    `app-tooltip__pointer--y-${props.positionY}`,
  ]
})

watch(
  () => props.active,
  (state) => {
    if (!state) return

    onPointerEnter()

    startDeactivationTimer()
  },
)

const startDeactivationTimer = () => {
  if (!props.deactivationTimeout) return

  setTimeout(() => (isActive.value = false), props.deactivationTimeout)
}

const getPointerPosition = () => {
  let top = ''
  let left = ''

  if (!props.pointer || !props.pointerOffset) {
    return { top, left }
  }

  if (props.positionY === 'center') {
    top = `${props.pointerOffset}px`
  }

  if (props.positionY === 'bottom' || props.positionY === 'top') {
    left = `${props.pointerOffset}px`
  }

  return { top, left }
}

const onUpdatePosition = async () => {
  if (
    !tooltipElement.value ||
    !tooltipContentElement.value ||
    !window ||
    !document
  )
    return

  const scrollTop = window.scrollY || document.body.scrollTop
  const scrollLeft = window.scrollX || document.body.scrollLeft

  const clientTop = document.body.clientTop || 0
  const clientLeft = document.body.clientLeft || 0

  const offsetTop = props.fixed ? 0 : scrollTop - clientTop
  const offsetLeft = props.fixed ? 0 : scrollLeft - clientLeft

  const viewportWidth = document.body.offsetWidth
  const viewportHeight = window.innerHeight

  const tooltipRect = tooltipElement.value.getBoundingClientRect()
  const tooltipContentRect = tooltipContentElement.value.getBoundingClientRect()

  let top = 0
  let left = 0

  // Определяем позицию тултипа по горизонтали в зависимости от переданного props
  if (props.positionX === 'right') {
    left = tooltipRect.left + offsetLeft
  }

  if (props.positionX === 'right' && props.positionY === 'center') {
    left = tooltipRect.left + tooltipRect.width + offsetLeft + props.offset
  }

  if (props.positionX === 'left') {
    left = tooltipRect.right - tooltipContentRect.width + offsetLeft
  }

  if (props.positionX === 'left' && props.positionY === 'center') {
    left =
      tooltipRect.left - tooltipContentRect.width - offsetLeft - props.offset
  }

  if (props.positionX === 'center') {
    left =
      tooltipRect.left +
      tooltipRect.width / 2 -
      tooltipContentRect.width / 2 +
      offsetLeft
  }

  // Определяем позицию тултипа по вертикали в зависимости от переданного props
  if (props.positionY === 'top') {
    top = tooltipRect.top - tooltipContentRect.height - props.offset + offsetTop
  }

  if (props.positionY === 'center') {
    top =
      tooltipRect.top - tooltipContentRect.height / 2 + offsetTop + props.offset
  }

  if (props.positionY === 'bottom') {
    top = tooltipRect.top + tooltipRect.height + offsetTop + props.offset
  }

  setTooltipPosition(top, left)

  await nextTick()

  const updatedTooltipRect = tooltipContentElement.value.getBoundingClientRect()

  // Определяем выходит ли тултип за пределы viewport по вертикали
  if (updatedTooltipRect.top < 0) {
    top = top - updatedTooltipRect.top + props.offset
  } else if (updatedTooltipRect.bottom > viewportHeight) {
    top = top - (updatedTooltipRect.bottom - viewportHeight) - props.offset
  }

  // Определяем выходит ли тултип за пределы viewport по горизонтали
  if (updatedTooltipRect.left < 0) {
    left = left - updatedTooltipRect.left + props.offset
  } else if (updatedTooltipRect.right > viewportWidth) {
    left = left - (updatedTooltipRect.right - viewportWidth) - props.offset
  }

  setTooltipPosition(top, left)
}

const setTooltipPosition = (top: number, left: number) => {
  tooltipContentPosition.value.top = `${top.toFixed(0)}px`
  tooltipContentPosition.value.left = `${left.toFixed(0)}px`
}

const onPointerEnter = async () => {
  isActive.value = true

  await nextTick()

  await onUpdatePosition()
}

const onPointerLeave = () => (isActive.value = false)

const outsideClick = (event: Event) => {
  if (!tooltipElement.value) return

  if (!tooltipElement.value.contains(event.target as HTMLElement)) {
    isActive.value = false
  }
}

onMounted(() => {
  window.parent.document.addEventListener('click', outsideClick)
  window.addEventListener('click', outsideClick)
  window.addEventListener('touchmove', onPointerLeave)

  if (!props.active) return

  onPointerEnter()

  startDeactivationTimer()
})

onBeforeUnmount(() => {
  window.parent.document.removeEventListener('click', outsideClick)
  window.removeEventListener('click', outsideClick)
  window.removeEventListener('touchmove', onPointerLeave)
})
</script>

<template>
  <div
    ref="tooltipElement"
    class="app-tooltip"
    @click="onPointerEnter"
    @mouseenter="onPointerEnter"
    @mouseleave="onPointerLeave"
  >
    <span v-if="$slots.activator" class="app-tooltip__activator">
      <slot name="activator" />
    </span>

    <Teleport to="body">
      <transition v-if="$slots.default" name="ui-fade">
        <div
          v-if="isActive"
          ref="tooltipContentElement"
          data-test-id="tooltip-content"
          :class="tooltipContentClasses"
          :style="tooltipContentStyles"
        >
          <img
            v-if="pointer"
            data-test-id="tooltip-pointer"
            :class="tooltipPointerClasses"
            :style="tooltipPointerStyles"
            :src="pointerIcon"
            alt="Иконка для указателя"
          />
          <slot />
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.app-tooltip {
  position: relative;
  display: inline-block;

  &__pointer {
    position: absolute;
    pointer-events: none;

    &--x-left {
      right: 16px;
    }

    &--x-center {
      left: calc(50% - 7px);
    }

    &--x-right {
      left: 16px;
    }

    &--y-top {
      bottom: -14px;
      transform: rotate(180deg);
    }

    &--y-bottom {
      top: -14px;
    }

    &--x-left#{&}--y-center {
      top: calc(50% - 7px);
      right: -14px;
      transform: rotate(90deg);
    }

    &--x-center#{&}--y-center {
      display: none;
    }

    &--x-right#{&}--y-center {
      top: calc(50% - 7px);
      left: -14px;
      transform: rotate(270deg);
    }
  }

  &__content {
    position: absolute;
    z-index: 10001;
    padding: 12px;
    font-family: 'Raleways', verdana, sans-serif;
    color: var(--ui-color-white);
    pointer-events: none;
    background-color: var(--ui-color-sandy-950);
    border-radius: 12px;
    transform: translateZ(1px);

    &--fixed {
      position: fixed !important;
    }

    &--styled {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
    }
  }
}
</style>
